import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Nav, NavItem, NavLink, Button } from "reactstrap";
import styled from "styled-components";
import CustomDataTable from "components/customs/table";
import { APP_URL } from "config";
import {
  getFormattedContactNumber,
  getFormattedOptions,
  getModifiedObject,
  getRegionOptions,
  hasPermission,
  Toast,
} from "utility/helpers";
import CommentsModal from "components/customs/comments-modal";
import Swal from "sweetalert2";
import { genericPost, uploadFile } from "services/services.general";
import {
  fetchAgencyBudgetAllocation,
  fetchComments,
  fetchCongressmanDetails,
  fetchCongressmanProjects,
  fetchCommitteeDetails,
  fetchDistricts,
  editCongDetails,
  fetchAllUpdateLogs,
  fetchAllRegions,
} from "services/services.congressman";
import {
  deleteRecipient,
  recipientBatchUpdate,
} from "services/services.projects";
import { defaultTableOption, rowPerPage } from "constants/table";
import { congRoles, SUPER_ADMIN_ACCOUNTS } from "utility/constants";
import { ALLOCATION_STATUS_OPTIONS, ALLOCATION_STATUS } from "constants/status";
import { fetchPartylists } from "services/services.affiliation";
import usePersistentCountdownTimer from "hooks/usePersistentTimer";
import { ArrowLeft } from "react-feather";
import DropdownSelect from "components/customs/dropdownSelect";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import { HeaderText } from "assets/js/Styles";
import { checkboxPromptForEndorsingAllocations } from "views/projects/constants";
import { fetchPendingProjects } from "../../../redux/actions/projects";
import {
  canEditRow,
  validate,
  isSpeakerViewOthersTab,
  getDropdownSelectOptions,
} from "./utility";
import EndorsementsTab from "./EndorsementsTab";
import CommitteeTab from "./CommitteeTab";
import { columnTable } from "./columns";
import CongressmanDetailsCard from "./DetailsCard";
import EndorsementFilters from "./EndorsementFilters";

const StyleTabs = styled(Nav)`
  justify-content: space-between;

  li {
    margin-bottom: 0;
    margin-top: 6px;
    margin-left: 0;
    margin-right: 0;
    cursor: pointer;

    a {
      color: black;
      font-weight: 500;

      &.active {
        font-weight: 600;
        color: #00316d !important;
        border: none !important;
        border-bottom: 2px solid #00316d !important;
        background: transparent !important;
      }

      :hover {
        border-color: transparent !important;
      }
    }
  }
`;

const GoBackButton = styled(Button)`
  margin-right: 16px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 50px;
`;

const TabSection = styled.div`
  margin-bottom: 1rem;
  margin-top: 16px;
`;

const EndorsementTabsContainer = styled.div`
  display: flex;
  margin-left: 0.5rem;
`;

const CarSection = styled.div`
  background: #ffffff;
  border-radius: 15px;
  margin-top: 16px;
  box-shadow: 4px 9px 17px -1px #00000014;
`;

const CardContainer = styled.div`
  padding-top: 24px;
  padding-left: 50px;
  padding-right: 50px;
  padding-bottom: 24px;
`;

const FilterSection = styled.div`
  margin-bottom: 16px;
  display: grid;
  grid-template-columns: 500px 250px;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
`;

const TableSection = styled.div`
  margin-top: 16px;
`;

const NavItemsContainer = styled.div`
  display: flex;
  margin-left: 20px;
  margin-right: 20px;
`;

const Main = () => {
  const { id, congID } = useParams();
  const location = useLocation();

  const navigate = useNavigate();
  const goBack = () => navigate(-1);

  const agency = location?.state?.agencyId || null;

  const [congDetails, setCongDetails] = useState({});
  const [selectorOptions, setSelectorOptions] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [totalBudget, setTotalBudget] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [recipientComments, setRecipientComments] = useState([]);
  const [editingCongressman, setEditingCongressman] = useState("");
  const [regions, setRegions] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [tempCongDetails, setTempCongDetails] = useState({});
  const [regionOptions, setRegionOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [parties, setParties] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null);

  const [isCommitteeShown, setIsCommitteeShown] = useState(false);
  const [committeeDetails, setCommitteeDetails] = useState({});
  const [activeTab, setActiveTab] = useState("1");

  const [activeEndorsementTab, setActiveEndorsementTab] =
    useState("my-endorsements");
  const [editingRowId, setEditingRowId] = useState();
  const [initialProjects, setInitialProjects] = useState([]);
  const [selectedDropdown, setSelectedDropdown] = useState(null);

  const [selectedStatusFilter, setSelectedStatusFilter] = useState();

  const [tableOption, setTableOption] = useState({
    ...defaultTableOption,
    currentRowsPerPage: rowPerPage,
  });

  const [projects, setProjects] = useState([]);
  const [logs, setLogs] = useState([]);

  // Tab contents configuration
  const tabContents = [
    {
      id: "1",
      key: "endorsements",
      label: "Endorsements",
      content: (
        <EndorsementsTab
          projects={projects}
          totalBudget={totalBudget}
        />
      ),
    },
    {
      id: "2",
      key: "committee",
      label: "Committee Membership",
      content: (
        <CommitteeTab
          committeeDetails={committeeDetails}
          isCommitteeShown={isCommitteeShown}
          toggleCommittee={() => setIsCommitteeShown(!isCommitteeShown)}
        />
      ),
    },
  ];

  let changeSearchTimeoutID;

  const dispatch = useDispatch();

  const { user } = useAuth();

  const role = user?.type;

  const isRoleCongressman = congRoles.includes(role);

  const { timer, toggleTimer } = usePersistentCountdownTimer();

  const dateRange = useSelector((state) => state.dateRange);
  const { dateFrom, dateTo } = dateRange || {};

  const isSpeakerViewOthers = useMemo(
    () => isSpeakerViewOthersTab(activeEndorsementTab),
    [activeEndorsementTab]
  );

  const toggleModal = () => setModalVisible(!modalVisible);

  const onChangeSearch = (event) => {
    event.persist();

    if (changeSearchTimeoutID) {
      clearTimeout(changeSearchTimeoutID);
    }

    changeSearchTimeoutID = setTimeout(() => {
      const eventValue = event.target.value;
      // reset editing row
      setEditingRowId();

      setTableOption({
        ...tableOption,
        page: 1,
        searchValue: eventValue.trim(),
      });
    }, 1000);
  };

  const onChangePage = (page) => {
    // reset editing row
    setEditingRowId();

    setTableOption({
      ...tableOption,
      page,
      offset: tableOption.currentRowsPerPage * page - 100,
    });
  };

  const onChangeRowsPerPage = (currentRowsPerPage, currentPage) => {
    // reset editing row
    setEditingRowId();

    setTableOption({
      ...tableOption,
      currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (column, sortDirection) => {
    // reset editing row
    setEditingRowId();

    setTableOption({
      ...tableOption,
      sort: {
        field: column.identifier,
        direction: sortDirection,
      },
    });
  };

  const handleSelectOnChange = (option) => {
    setSelectedAgency(option);
    setSelectedDropdown(option);
  };

  const handleCancelClicked = (index) => {
    setEditingRowId("");
    setEditingCongressman("");

    const newData = [...tableOption.data];
    newData[index] = initialProjects[index];

    setTableOption({
      ...tableOption,
      data: newData,
    });
  };

  const fetchProjects = () => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    const params = {
      limit: tableOption.currentRowsPerPage,
      offset: tableOption.offset,
      search: tableOption.searchValue,
      column: tableOption.sort.field,
      order: tableOption.sort.direction,
      startDate: dateFrom || "",
      endDate: dateTo || "",
    };

    if (role !== "Agency") {
      params.status = selectedStatusFilter?.map((item) => item.value).join(";");
    }

    // Use different endpoint only for Others tab in speaker-view
    // My Endorsements tab always uses the original endpoint
    const url = isSpeakerViewOthers
      ? `/congressman/${congID}/other-recipients`
      : `/congressman/${congID}/agencies/${selectedAgency?.ID}/recipients`;

    fetchCongressmanProjects({
      url,
      params,
      callback: (data) => {
        setTableOption({
          ...tableOption,
          isLoading: false,
          totalRows: data?.count,
          data: data?.rows,
        });
        setInitialProjects(data?.rows);
      },
    });
  };

  const fetchData = () => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    fetchCongressmanDetails({
      url: `/congressman/${congID}`,
      params: {
        startDate: dateFrom || "",
        endDate: dateTo || "",
      },
      callback: (data) => {
        const newData = data;
        newData.cosContact = getFormattedContactNumber(newData.cosContact);
        newData.contact = getFormattedContactNumber(newData.contact);
        setCongDetails(data);
        setTempCongDetails(newData);

        if (data.Projects.length === 0) {
          setTableOption({
            ...tableOption,
            isLoading: false,
            data: [],
          });

          setSelectedAgency(null);
          setSelectedDropdown("");
          setSelectorOptions([]);
        }
      },
    });
  };

  const handleChangeFilter = (selected) => {
    setSelectedStatusFilter(selected || []);
  };

  const handleSaveStatusChange = (data, index) => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    const initialData = initialProjects[index];
    const newData = {};

    // eslint-disable-next-line array-callback-return
    Object.keys(initialData)?.map((obj) => {
      if (initialData[obj] !== data[obj]) {
        if (obj === "budget_allocation") {
          newData.budget = data[obj];
        } else if (obj === "implementing") {
          newData.implementingAgency = data[obj].id;
        } else if (obj === "status") {
          //
        } else {
          newData[obj] = data[obj];
        }
      }
    });

    const finalData = {};
    finalData.status = data?.status;
    finalData.excludeCongAndCOS = Boolean(data.excludeCongAndCOS);
    finalData.isSpeakerVisible = Boolean(data.showEndorsementToSpeaker);
    finalData.sendOnlyToSuperAdmin = Boolean(data.sendOnlyToSuperAdmin);

    finalData.recipients = [
      {
        recipientId: data?.id,
        ...newData,
      },
    ];

    recipientBatchUpdate({
      url: "/recipient/batch-update/status",
      formData: finalData,
      callback: ({ isSuccess, msg }) => {
        if (isSuccess) {
          fetchProjects();

          if (SUPER_ADMIN_ACCOUNTS.includes(role)) {
            dispatch(fetchPendingProjects());
          }

          const text = msg.replace(/\n/g, "<br>");

          Swal.fire({
            title: text.includes("<br>") ? "" : "Success!",
            icon: "success",
            html: text,
            customClass: text.includes("<br>")
              ? { container: "sms-notif" }
              : null,
          });
        } else {
          handleCancelClicked(index);
        }
      },
    });
  };

  const handleRemarks = (text, recipientId, url) => {
    genericPost({
      url: "/comment",
      formData: {
        text,
        recipientId,
        newsId: null,
      },
      callback: () => {
        //
      },
    });

    genericPost({
      url,
      formData: {
        remarks: text,
        recipientId,
      },
      callback: () => {
        //
      },
    });
  };

  const handleDeleteAction = (row, idx) => {
    Swal.fire({
      title: "Warning",
      // eslint-disable-next-line max-len
      text: "Are you sure you want to delete this requested budget?",
      icon: "warning",
      confirmButtonText: "OK",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteRecipient({
          params: row?.id,
          callback: ({ isSuccess }) => {
            if (isSuccess) {
              Toast({
                type: 1,
                content: "Successfully deleted recipient.",
              });
              fetchProjects();
            } else {
              Toast({
                type: 4,
                content: "An error occurred.",
              });
            }
          },
        });
      } else if (idx !== undefined && idx !== null) {
        handleSaveStatusChange(row, idx);
      }
    });
  };

  // eslint-disable-next-line consistent-return
  const handleStatusChange = async (index, option) => {
    const newData = [...tableOption.data];
    const oldValue = newData[index].status;
    const newValue = option.value;

    const oldStatus = ALLOCATION_STATUS_OPTIONS.find(
      (status) => status.value === newData[index].status
    ).label;

    if (oldValue.toLowerCase() !== newValue.toLowerCase()) {
      try {
        const checkbox = {};

        if (newValue === ALLOCATION_STATUS.APPROVED) {
          checkbox.html = checkboxPromptForEndorsingAllocations(
            oldStatus,
            option.label,
            role
          );

          checkbox.preConfirm = () => {
            const checkbox2 = document.getElementById("swal-checkbox2")?.checked
              ? 1
              : 0;

            const checkbox3 = document.getElementById("swal-checkbox3")?.checked
              ? 1
              : 0;

            return {
              excludeCongAndCOS: 0,
              showEndorsementToSpeaker: checkbox2,
              sendOnlyToSuperAdmin: checkbox3,
            };
          };
          checkbox.focusConfirm = false;
        }

        const result = await Swal.fire({
          title: "Warning",
          // eslint-disable-next-line max-len
          text: `Are you sure you want to change the status from ${oldStatus} to ${option.label}?`,
          icon: "warning",
          confirmButtonText: "OK",
          showCancelButton: true,
          ...checkbox,
        });

        if (result.isConfirmed) {
          newData[index] = {
            ...newData[index],
            status: newValue,
            excludeCongAndCOS: result.value?.excludeCongAndCOS ?? 0,
            sendOnlyToSuperAdmin: result.value?.sendOnlyToSuperAdmin ?? 0,
            showEndorsementToSpeaker:
              result.value?.showEndorsementToSpeaker ?? 0,
          };

          setTableOption({
            ...tableOption,
            data: newData,
          });

          if (
            newValue === ALLOCATION_STATUS.RESUBMISSION ||
            newValue === ALLOCATION_STATUS.NOTELIGIBLE
          ) {
            const { value: text } = await Swal.fire({
              input: "textarea",
              inputLabel: "Remarks",
              inputPlaceholder: "Type your remarks here...",
              inputAttributes: {
                "aria-label": "Type your remarks here",
              },
              showCancelButton: true,
            });

            if (text) {
              newData[index] = {
                ...newData[index],
                remarks: text,
              };

              const url =
                newValue === ALLOCATION_STATUS.RESUBMISSION
                  ? "/recipient/send-resubmission-notification"
                  : "/recipient/send-not-eligible-notification";
              handleRemarks(text, newData[index].id, url);
            } else return;
          }

          if (
            newValue === ALLOCATION_STATUS.NOTELIGIBLE &&
            SUPER_ADMIN_ACCOUNTS.includes(role)
          ) {
            const res = await Swal.fire({
              title: "Warning",
              text: "Not Eligible allocations are opt to be deleted. Do you wish to proceed?",
              icon: "info",
              confirmButtonText: "OK",
              showCancelButton: true,
            });

            if (res.isConfirmed) {
              handleDeleteAction(newData[index], index);

              return;
            }
          }

          handleSaveStatusChange(newData[index], index);
        } else {
          newData[index] = {
            ...newData[index],
            status: oldValue,
          };

          setTableOption({
            ...tableOption,
            data: newData,
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
  };

  const fetchCommitteeTable = () => {
    fetchCommitteeDetails({
      params: congID,
      callback: (data) => {
        setCommitteeDetails(data);
      },
    });
  };

  const fetchRecipientMsgs = (rowId) => {
    fetchComments({
      params: rowId,
      callback: (data) => {
        setRecipientComments(data);
      },
    });
  };

  const fetchBudgetAllocation = () => {
    fetchAgencyBudgetAllocation({
      params: {
        cong: congID,
        agency: selectedDropdown?.ID,
        startDate: dateFrom || "",
        endDate: dateTo || "",
      },
      callback: (data) => {
        const newData = data?.projects?.length !== 0 ? data?.projects : [];

        const total = newData?.reduce((sum, proj) => {
          return sum + proj.allocatedBudget;
        }, 0);

        setTotalBudget(total);

        const projs = newData?.map((proj) => {
          return {
            id: proj?.id,
            name: proj?.name,
            value: proj.allocatedBudget,
            budget:
              proj.allocatedBudget !== 0 || proj.allocatedBudget !== null
                ? (proj.allocatedBudget / total) * 100
                : 0,
          };
        });

        setProjects(projs);
      },
    });
  };

  const fetchAllAffiliation = () => {
    fetchPartylists({
      callback: (Data) => {
        if (Array.isArray(Data) && Data?.length !== 0) {
          const options = getFormattedOptions("acronym", Data);
          setParties(options);
        }
      },
    });
  };

  const fetchRegions = () => {
    fetchAllRegions({
      params: {
        includeCentral: true,
      },
      callback: (Data) => {
        if (Array.isArray(Data) && Data?.length !== 0) {
          const options = getFormattedOptions("acronym", Data);
          setRegions(options);

          const regionalOptions = getRegionOptions(Data);
          setRegionOptions(regionalOptions);
        }
      },
    });
  };

  const fetchAllDistrict = () => {
    fetchDistricts({
      callback: (Data) => {
        if (Array.isArray(Data) && Data?.length !== 0) {
          setDistricts(Data);
        }
      },
    });
  };

  const handleBumpIconOnClicked = async (congId, status) => {
    const result = await Swal.fire({
      title: "Warning",
      text: "Would you like to bump this allocation?",
      icon: "warning",
      confirmButtonText: "Yes",
      showCancelButton: true,
    });

    if (result.isConfirmed) {
      setTableOption({
        ...tableOption,
        isLoading: true,
      });

      toggleTimer(congId);

      genericPost({
        url: "/comment",
        formData: {
          text: "bumped this notification.",
          recipientId: congId,
        },
        callback: () => {
          //
        },
      });

      genericPost({
        url: "/recipient/bump-allocation-notification",
        formData: {
          status,
          recipientId: congId,
        },
        callback: ({ isSuccess }) => {
          if (isSuccess) {
            fetchProjects();
            fetchComments(congId);
          }
        },
      });
    }
  };

  // revert changes
  const handleFailedCongUpdate = () => {
    setIsEditMode(false);
    setTempCongDetails(congDetails);

    const region = regionOptions.find(
      (__region) => __region.id === congDetails.District.Region.id
    );

    setSelectedRegion(region);

    const district = districts.find(
      (__district) => __district.id === congDetails.DistrictId
    );
    setSelectedDistrict(district);

    const partylist = parties.find((party) => party.id === congDetails.PartyId);
    setSelectedParty(partylist);

    setTableOption({
      ...tableOption,
      isLoading: false,
    });
  };

  const fetchLogs = async () => {
    await fetchAllUpdateLogs({
      param: congID,
      callback: (data) => {
        if (data) {
          setLogs(data);
        }
      },
    });
  };

  const uploadImage = async () => {
    let fileName = "";

    await uploadFile({
      file: tempCongDetails.photo,
      fileName: tempCongDetails.photoName,
      onProgress: () => {
        //
      },
      callback: (data) => {
        if (data) fileName = data;
      },
    });

    return fileName;
  };

  const handleSaveButtonClicked = async () => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    const isValid = await validate(
      selectedDistrict,
      selectedRegion,
      tempCongDetails
    );

    if (typeof isValid === "boolean") {
      const data = {
        committeeChairmanship: tempCongDetails.committeeChairmanship,
        DistrictId: selectedDistrict?.id || tempCongDetails.DistrictId,
        PartyId: selectedParty?.id || tempCongDetails.PartyId,
        RegionId: selectedRegion?.id || tempCongDetails.District.RegionId,
        termOfOffice: tempCongDetails.termOfOffice,
        birthdate: tempCongDetails.birthdate,
        contact: tempCongDetails.contact,
        cosFirstName: tempCongDetails.cosFirstName,
        cosLastName: tempCongDetails.cosLastName,
        cosContact: tempCongDetails.cosContact,
        // eslint-disable-next-line max-len
        fullName: `HON. ${tempCongDetails.firstName} ${tempCongDetails.middleInitial} ${tempCongDetails.lastName}`,
        firstName: tempCongDetails.firstName,
        middleInitial: tempCongDetails.middleInitial,
        lastName: tempCongDetails.lastName,
      };

      const initialData = {
        committeeChairmanship: congDetails.committeeChairmanship,
        DistrictId: congDetails.DistrictId,
        PartyId: congDetails.PartyId,
        RegionId: congDetails.District.RegionId,
        termOfOffice: congDetails.termOfOffice,
        birthdate: congDetails.birthdate,
        contact: congDetails.contact,
        cosFirstName: congDetails.cosFirstName,
        cosLastName: congDetails.cosLastName,
        cosContact: congDetails.cosContact,
        fullName: congDetails.fullName,
        firstName: congDetails.firstName,
        middleInitial: congDetails.middleInitial,
        lastName: congDetails.lastName,
        photo: congDetails.photo,
      };

      if (typeof tempCongDetails.photo !== "string") {
        const fileName = await uploadImage();
        data.photo = fileName;
      }

      const newData = await getModifiedObject(initialData, data);

      await editCongDetails({
        url: `${APP_URL}/congressman/${congID}`,
        formData: newData,
        callback: ({ isSuccess }) => {
          if (isSuccess) {
            fetchData();
            setIsEditMode(false);
            if (isRoleCongressman) fetchLogs();

            Toast({
              type: 1,
              content: "Successfully edited cong details",
            });
          } else {
            handleFailedCongUpdate();

            Toast({
              type: 4,
              content: "An error occurred.",
            });
          }
        },
      });
    } else {
      handleFailedCongUpdate();

      setTableOption({
        ...tableOption,
        isLoading: false,
      });

      Toast({
        type: 4,
        content: isValid,
      });
    }
  };

  const columns = columnTable({
    id,
    congID,
    editingRowId,
    tableOption,
    setTableOption,
    selectedAgency,
    role,
    handleStatusChange,
    initialProjects,
    regions,
    toggleModal,
    setEditingCongressman,
    fetchRecipientMsgs,
    canEditRow,
    congDetails,
    timer,
    handleBumpIconOnClicked,
    isRoleCongressman,
    handleDeleteAction,
    navigate,
    activeEndorsementTab,
  });

  useEffect(() => {
    fetchData();
  }, [dateFrom]);

  useEffect(() => {
    if (congDetails?.Projects && congDetails?.Projects?.length !== 0) {
      let options = [];

      // eslint-disable-next-line array-callback-return
      congDetails?.Projects?.map((proj) => {
        if (
          proj?.Agency &&
          !options.find((option) => option?.id === proj?.Agency?.id)
        ) {
          options.push(proj?.Agency);
        }
      });

      options = getDropdownSelectOptions(options);

      if (agency) {
        const option = options?.find((op) => op.ID === agency);

        if (option) {
          setSelectedAgency(option);
        }
      } else {
        setSelectedAgency(options[0]);
        setSelectedDropdown(options[0]);
      }

      setSelectorOptions(options);
    }
  }, [congDetails]);

  useEffect(() => {
    if (selectedAgency || isSpeakerViewOthers) {
      fetchProjects();
    }
  }, [
    selectedAgency,
    tableOption.currentRowsPerPage,
    tableOption.offset,
    tableOption.searchValue,
    tableOption.sort.direction,
    tableOption.sort.field,
    selectedStatusFilter,
    dateFrom,
    isSpeakerViewOthers,
  ]);

  useEffect(() => {
    fetchCommitteeTable();
    fetchRegions();
    fetchAllDistrict();
    fetchAllAffiliation();

    if (isRoleCongressman) fetchLogs();
  }, []);

  useEffect(() => {
    // fetch budget allocation under specific agency
    if (selectedDropdown) {
      fetchBudgetAllocation();
    }
  }, [selectedDropdown, dateFrom]);

  useEffect(() => {
    if (selectedRegion !== null && districts.length !== 0) {
      const options = districts.filter(
        (district) => district?.RegionId === selectedRegion.id
      );

      const formattedDistrictOptions = getFormattedOptions("name", options);

      setDistrictOptions(formattedDistrictOptions);

      if (selectedRegion.id !== congDetails.District.RegionId) {
        setSelectedDistrict(null);
      }

      const selectedOption = formattedDistrictOptions.find(
        (district) => district.id === congDetails.DistrictId
      );
      setSelectedDistrict(selectedOption);
    }
  }, [selectedRegion, districts]);

  useEffect(() => {
    if (
      regionOptions.length !== 0 &&
      Object.keys(congDetails).length !== 0 &&
      selectedRegion === null &&
      parties.length !== 0
    ) {
      const selectedOption = regionOptions.find(
        (region) => region.id === congDetails.District.Region.id
      );

      setSelectedRegion(selectedOption);

      const partylist = parties.find(
        (party) => party.id === congDetails.PartyId
      );
      setSelectedParty(partylist);
    }
  }, [regionOptions, congDetails, parties]);

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <>
      <HeaderSection>
        <GoBackButton
          onClick={() => goBack()}
          aria-label="Go back"
          color="primary"
          outline
        >
          <ArrowLeft />
        </GoBackButton>

        <HeaderText>{"Projects & Programs"}</HeaderText>
      </HeaderSection>
      <CongressmanDetailsCard
        congDetails={congDetails}
        logs={logs}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        tempCongDetails={tempCongDetails}
        setTempCongDetails={setTempCongDetails}
        selectedRegion={selectedRegion}
        regionOptions={regionOptions}
        setSelectedRegion={setSelectedRegion}
        districtOptions={districtOptions}
        selectedDistrict={selectedDistrict}
        setSelectedDistrict={setSelectedDistrict}
        tableOption={tableOption}
        handleSaveButtonClicked={handleSaveButtonClicked}
        selectedParty={selectedParty}
        parties={parties}
        setSelectedParty={setSelectedParty}
      />

      {hasPermission("committee-view") && (
        <TabSection>
          <StyleTabs tabs>
            <NavItemsContainer>
              {tabContents.map((tab) => (
                <NavItem key={tab.id}>
                  <NavLink
                    className={activeTab === tab.id ? "active" : ""}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </NavLink>
                </NavItem>
              ))}
            </NavItemsContainer>
          </StyleTabs>

          {tabContents.find((tab) => tab.id === activeTab)?.content}
        </TabSection>
      )}

      <CarSection>
        <CardContainer>
          {hasPermission("speaker-view") && (
            <TabSection>
              <StyleTabs tabs>
                <EndorsementTabsContainer>
                  <NavItem>
                    <NavLink
                      className={
                        activeEndorsementTab === "my-endorsements"
                          ? "active"
                          : ""
                      }
                      onClick={() => setActiveEndorsementTab("my-endorsements")}
                    >
                      {"My Endorsements"}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={
                        activeEndorsementTab === "others" ? "active" : ""
                      }
                      onClick={() => setActiveEndorsementTab("others")}
                    >
                      {"Others"}
                    </NavLink>
                  </NavItem>
                </EndorsementTabsContainer>
              </StyleTabs>
            </TabSection>
          )}

          {!isSpeakerViewOthers && (
            <FilterSection>
              {/* agency select dropdown here */}
              <DropdownSelect
                isLoading={tableOption.isLoading}
                isDisabled={tableOption.isLoading}
                onChange={(option) => handleSelectOnChange(option)}
                value={selectedAgency}
                options={selectorOptions}
                filterOption={(option, searchValue) =>
                  option?.data?.acronym
                    ?.toLowerCase()
                    ?.includes(searchValue?.toLowerCase())
                }
              />
            </FilterSection>
          )}

          <EndorsementFilters
            tableOption={tableOption}
            onChangeSearch={onChangeSearch}
            handleChangeFilter={handleChangeFilter}
            selectedStatusFilter={selectedStatusFilter}
          />

          <TableSection>
            <CustomDataTable
              columns={columns}
              data={tableOption.data}
              noHeader
              onChangePage={onChangePage}
              onChangeRowsPerPage={onChangeRowsPerPage}
              onSort={onSort}
              paginationPerPage={tableOption.currentRowsPerPage}
              paginationServer
              paginationTotalRows={tableOption.totalRows}
              progressPending={tableOption.isLoading}
              sortServer
              defaultSortField={tableOption.sort.field}
              defaultSortAsc={tableOption.sort.direction}
            />
          </TableSection>
        </CardContainer>
      </CarSection>

      <CommentsModal
        isOpen={modalVisible}
        handleClose={toggleModal}
        fetchComments={fetchRecipientMsgs}
        comments={recipientComments}
        congID={editingCongressman}
        congressmanId={congID}
        selectedAgency={selectedAgency}
        handleFetchTable={fetchProjects}
      />
    </>
  );
};

export default Main;
